import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import api from '../../api/client.js';

const PERIODS = [
  { key: 'week', label: 'This week' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
];
const FUNCS = [
  { key: 'all',          label: 'All' },
  { key: 'bdm',          label: 'BDM' },
  { key: 'aftermarket',  label: 'Aftermarket' },
  { key: 'equipment',    label: 'Equipment Sales' },
  { key: 'unclassified', label: 'Unclassified' },
];
const FUNC_LABEL = { bdm: 'BDM', aftermarket: 'Aftermarket', equipment: 'Equipment' };

function money(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}k`;
  return `$${Math.round(v)}`;
}
function daysAgo(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

const COLS = [
  { key: 'notes',              label: 'Notes',   group: 'Activity' },
  { key: 'visits',             label: 'Visits',  group: 'Activity' },
  { key: 'calls',              label: 'Calls',   group: 'Activity' },
  { key: 'customers_touched',  label: 'Cust',    group: 'Activity', title: 'Distinct customers touched in period' },
  { key: 'active_days',        label: 'Days',    group: 'Activity', title: 'Distinct days active in period' },
  { key: 'last_active_at',     label: 'Last',    group: 'Activity', title: 'Days since last note' },
  { key: 'app_notes',          label: 'App',     group: 'Adoption', title: 'Notes logged directly in the app (voice/typed), all-time' },
  { key: 'email_notes',        label: 'Email',   group: 'Adoption', title: 'Notes ingested from call-report emails, all-time' },
  { key: 'last_app_note_at',   label: 'Last App',group: 'Adoption', title: 'Days since last app note — N/A means the rep has never logged one in the app' },
  { key: 'book_size',          label: 'Book',    group: 'Coverage', title: 'Distinct customers this rep has ever touched' },
  { key: 'cold_accounts',      label: 'Cold',    group: 'Coverage', title: 'Book accounts the rep has not touched in 60+ days' },
  { key: 'declining_accounts', label: 'Declng',  group: 'Coverage', title: 'Book accounts whose sales are down vs prior 12mo' },
  { key: 'overdue_followups',  label: 'Overdue', group: 'Coverage', title: 'Open follow-ups past their due date' },
  { key: 'followups_created',  label: 'F/U',     group: 'Outcomes', title: 'Follow-ups created in period' },
  { key: 'opps_from_notes',    label: 'Opps*',   group: 'Outcomes', title: 'Opportunities created from notes in period' },
  { key: 'open_value',         label: 'Open $',  group: 'Outcomes', title: 'Open pipeline value', money: true },
  { key: 'won_value',          label: 'Won $',   group: 'Outcomes', title: 'Won value in period', money: true },
  { key: 'conversion_pct',     label: 'Conv %',  group: 'Outcomes', title: 'Notes that led to a follow-up or opportunity' },
];
const GROUP_COLOR = { Activity: 'bg-navy/5', Adoption: 'bg-sky-50', Coverage: 'bg-amber-50', Outcomes: 'bg-emerald-50' };

// App-adoption status: green = used today, yellow = used within the stale-followup
// window (14d, matching pull-bdm-call-reports.js's STALE_FOLLOWUP_DAYS), red = either
// stale beyond that or (day == null) never logged a note in the app at all.
function appDotStatus(lastAppNoteAt) {
  const d = daysAgo(lastAppNoteAt);
  if (d == null) return { dot: '🔴', cls: 'text-crimson font-medium', label: 'N/A' };
  if (d === 0) return { dot: '🟢', cls: 'text-emerald-700', label: 'Today' };
  if (d <= 14) return { dot: '🟡', cls: 'text-amber-700', label: `${d}d` };
  return { dot: '🔴', cls: 'text-crimson font-medium', label: `${d}d` };
}

export default function BdmPerformance() {
  const [period, setPeriod] = useState('30d');
  const [func, setFunc] = useState('all');
  const [sortKey, setSortKey] = useState('notes');
  const [sortDir, setSortDir] = useState('desc');
  const nav = useNavigate();

  const q = useQuery({
    queryKey: ['bdm-performance', period],
    queryFn: () => api.get(`/admin/bdm-performance?period=${period}`).then(r => r.data.data),
  });

  const counts = useMemo(() => {
    const c = { all: 0, bdm: 0, aftermarket: 0, equipment: 0, unclassified: 0 };
    for (const r of q.data || []) {
      c.all++;
      c[r.sales_function || 'unclassified']++;
    }
    return c;
  }, [q.data]);

  const rows = useMemo(() => {
    let data = [...(q.data || [])];
    if (func === 'unclassified') data = data.filter(r => !r.sales_function);
    else if (func !== 'all') data = data.filter(r => r.sales_function === func);
    const dir = sortDir === 'asc' ? 1 : -1;
    data.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'last_active_at' || sortKey === 'last_app_note_at') { av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0; }
      av = av ?? -1; bv = bv ?? -1;
      return av < bv ? -dir : av > bv ? dir : 0;
    });
    return data;
  }, [q.data, func, sortKey, sortDir]);

  function onSort(key) {
    if (key === sortKey) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  }

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="section-eyebrow">Management</div>
          <h1 className="text-2xl">Sales Team Performance</h1>
          <div className="rule-crimson" />
          <p className="text-sm text-muted">Who's working, covering their book, and turning notes into outcomes — by function.</p>
        </div>
        <div className="flex gap-1">
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className={clsx('btn px-3 py-1 text-sm', period === p.key ? 'bg-navy text-white' : 'bg-panel text-navy')}>
              {p.label}
            </button>
          ))}
        </div>
      </header>

      {/* Function filter */}
      <div className="flex gap-1.5 flex-wrap">
        {FUNCS.map(f => (
          <button key={f.key} onClick={() => setFunc(f.key)}
            className={clsx('btn px-2.5 py-1 text-xs', func === f.key ? 'bg-crimson text-white' : 'bg-panel text-navy')}>
            {f.label} <span className="opacity-70">({counts[f.key] ?? 0})</span>
          </button>
        ))}
      </div>

      {q.isLoading && <div className="text-muted text-sm">Loading…</div>}
      {q.isError && <div className="text-crimson text-sm">Failed to load.</div>}

      {q.data && (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider">
                <th className="p-2 text-left" colSpan={2}></th>
                {['Activity', 'Adoption', 'Coverage', 'Outcomes'].map(g => {
                  const span = COLS.filter(c => c.group === g).length;
                  return <th key={g} colSpan={span} className={clsx('p-1 text-center text-muted border-l border-panel', GROUP_COLOR[g])}>{g}</th>;
                })}
              </tr>
              <tr className="text-[11px] text-muted border-b border-navy-100">
                <th className="p-2 text-left font-medium">Rep</th>
                <th className="p-2 text-left font-medium">Function</th>
                {COLS.map((c, i) => (
                  <th key={c.key} title={c.title}
                      onClick={() => onSort(c.key)}
                      className={clsx('p-2 text-right cursor-pointer select-none hover:text-navy',
                        (i === 0 || COLS[i - 1].group !== c.group) && 'border-l border-panel',
                        sortKey === c.key && 'text-navy font-semibold')}>
                    {c.label}{sortKey === c.key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const idle = r.notes === 0;
                const last = daysAgo(r.last_active_at);
                return (
                  <tr key={r.id} onClick={() => nav(`/admin/reps/${r.id}`)}
                      className={clsx('border-b last:border-0 cursor-pointer hover:bg-panel', idle && 'opacity-60')}>
                    <td className="p-2">
                      <div className="font-medium text-navy">{r.full_name}</div>
                      {r.branch_name && <div className="text-[10px] text-muted">{r.branch_name}</div>}
                    </td>
                    <td className="p-2">
                      {r.sales_function
                        ? <span className={clsx('text-[10px] px-1.5 py-0.5 rounded',
                            r.sales_function === 'bdm' ? 'bg-navy/10 text-navy'
                            : r.sales_function === 'aftermarket' ? 'bg-amber-100 text-amber-900'
                            : 'bg-emerald-100 text-emerald-900')}>
                            {FUNC_LABEL[r.sales_function]}
                          </span>
                        : <span className="text-[10px] text-crimson">unclassified</span>}
                    </td>
                    <Num v={r.notes} bold zeroWarn border />
                    <Num v={r.visits} />
                    <Num v={r.calls} />
                    <Num v={r.customers_touched} />
                    <Num v={r.active_days} />
                    <td className={clsx('p-2 text-right', last != null && last > 14 ? 'text-crimson font-medium' : 'text-muted')}>
                      {last == null ? '—' : `${last}d`}
                    </td>
                    <td className="p-2 text-right tabular-nums border-l border-panel">{r.app_notes || (r.app_notes === 0 ? '0' : '—')}</td>
                    <td className="p-2 text-right tabular-nums text-muted">{r.email_notes || '—'}</td>
                    {(() => {
                      const s = appDotStatus(r.last_app_note_at);
                      return (
                        <td className={clsx('p-2 text-right', s.cls)} title={r.last_app_note_at ? new Date(r.last_app_note_at).toLocaleDateString() : 'Never logged a note in the app'}>
                          {s.dot} {s.label}
                        </td>
                      );
                    })()}
                    <td className="p-2 text-right border-l border-panel">{r.book_size || '—'}</td>
                    <td className={clsx('p-2 text-right', r.cold_accounts > 0 && 'text-amber-700 font-medium')}>{r.cold_accounts || '—'}</td>
                    <td className={clsx('p-2 text-right', r.declining_accounts > 0 && 'text-amber-700 font-medium')}>{r.declining_accounts || '—'}</td>
                    <td className={clsx('p-2 text-right', r.overdue_followups > 0 && 'text-crimson font-medium')}>{r.overdue_followups || '—'}</td>
                    <td className="p-2 text-right border-l border-panel">{r.followups_created || '—'}</td>
                    <td className="p-2 text-right">{r.opps_from_notes || '—'}</td>
                    <td className="p-2 text-right tabular-nums">{r.open_value ? money(r.open_value) : '—'}</td>
                    <td className="p-2 text-right tabular-nums">{r.won_value ? money(r.won_value) : '—'}</td>
                    <td className={clsx('p-2 text-right font-medium',
                      r.conversion_pct == null ? 'text-muted'
                      : r.conversion_pct >= 50 ? 'text-emerald-700'
                      : r.conversion_pct < 25 ? 'text-amber-700' : 'text-navy')}>
                      {r.conversion_pct == null ? '—' : `${r.conversion_pct}%`}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={COLS.length + 2} className="p-4 text-center text-muted text-sm">No reps in this group.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[11px] text-muted space-y-0.5">
        <div>Click a rep to open their full deep-dive. Click a column header to sort. Zero-activity unclassified accounts are hidden.</div>
        <div><b>Cold</b> = book accounts untouched 60+ days · <b>Declng</b> = sales down vs prior 12mo · <b>Opps*</b> = opportunities created from notes · <b>Conv %</b> = notes that led to a follow-up or opportunity (the "useful work" signal).</div>
        <div><b>App</b> / <b>Email</b> = notes logged in the app (voice/typed) vs. ingested from call-report emails, all-time · <b>Last App</b> = 🟢 today · 🟡 within 14 days · 🔴 stale beyond 14 days or <b>N/A</b> if never logged in the app.</div>
      </div>
    </div>
  );
}

function Num({ v, bold, zeroWarn, border }) {
  return (
    <td className={clsx('p-2 text-right tabular-nums', bold && 'font-semibold', border && 'border-l border-panel',
      zeroWarn && (v === 0) && 'text-crimson')}>
      {v || (zeroWarn ? '0' : '—')}
    </td>
  );
}
